import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const s = {
  page: { minHeight: "100vh", background: "#f1f5f9", paddingTop: "80px", paddingBottom: "40px" },
  container: { maxWidth: "1100px", margin: "0 auto", padding: "0 20px" },
  card: { background: "white", borderRadius: "12px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" },
  btn: (bg = "#0056D2", color = "white") => ({ padding: "8px 16px", background: bg, color, border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600", fontFamily: "inherit" }),
  tab: (active) => ({ padding: "10px 20px", background: active ? "#0056D2" : "white", color: active ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: "8px", cursor: "pointer", fontWeight: "600", fontSize: "14px", fontFamily: "inherit", marginRight: "8px" }),
  input: { width: "100%", padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "14px", fontFamily: "inherit", boxSizing: "border-box", marginBottom: "10px" },
  badge: (s) => {
    const map = { pending: "#fef3c7:#92400e", reviewed: "#dbeafe:#1e40af", accepted: "#d1fae5:#065f46", rejected: "#fee2e2:#991b1b" };
    const [bg, color] = (map[s] || "#f3f4f6:#374151").split(":");
    return { padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", background: bg, color };
  },
};

export default function Admin() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [courses, setCourses] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [msg, setMsg] = useState("");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    if (!token || role !== "admin") { navigate("/login"); return; }
    fetchStats();
  }, []);

  useEffect(() => {
    if (tab === "dashboard") fetchStats();
    if (tab === "applications") fetchApplications();
    if (tab === "courses") fetchCourses();
    if (tab === "services") fetchServices();
    if (tab === "users") fetchUsers();
  }, [tab]);

  const fetchStats = async () => {
    const r = await fetch(`${API}/api/admin/stats`, { headers });
    if (r.ok) setStats(await r.json());
  };
  const fetchApplications = async () => {
    const r = await fetch(`${API}/api/admin/applications?status=${filterStatus}`, { headers });
    if (r.ok) setApplications(await r.json());
  };
  const fetchCourses = async () => {
    const r = await fetch(`${API}/api/admin/courses`, { headers });
    if (r.ok) setCourses(await r.json());
  };
  const fetchServices = async () => {
    const r = await fetch(`${API}/api/admin/services`, { headers });
    if (r.ok) setServices(await r.json());
  };
  const fetchUsers = async () => {
    const r = await fetch(`${API}/api/admin/users`, { headers });
    if (r.ok) setUsers(await r.json());
  };

  useEffect(() => { if (tab === "applications") fetchApplications(); }, [filterStatus]);

  const updateAppStatus = async (id, status) => {
    await fetch(`${API}/api/admin/applications/${id}`, { method: "PATCH", headers, body: JSON.stringify({ status }) });
    fetchApplications(); fetchStats();
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await fetch(`${API}/api/admin/applications/${id}`, { method: "DELETE", headers });
    fetchApplications(); fetchStats();
  };

  const saveCourse = async () => {
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API}/api/admin/courses/${editing}` : `${API}/api/admin/courses`;
    const r = await fetch(url, { method, headers, body: JSON.stringify(formData) });
    if (r.ok) { setMsg("✅ Saved!"); setShowForm(false); setEditing(null); setFormData({}); fetchCourses(); }
    else setMsg("❌ Error saving");
  };

  const deleteCourse = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    await fetch(`${API}/api/admin/courses/${id}`, { method: "DELETE", headers });
    fetchCourses();
  };

  const saveService = async () => {
    const method = editing ? "PUT" : "POST";
    const url = editing ? `${API}/api/admin/services/${editing}` : `${API}/api/admin/services`;
    const featuresArr = typeof formData.features === "string"
      ? formData.features.split(",").map(f => f.trim()).filter(Boolean)
      : formData.features || [];
    const r = await fetch(url, { method, headers, body: JSON.stringify({ ...formData, features: featuresArr }) });
    if (r.ok) { setMsg("✅ Saved!"); setShowForm(false); setEditing(null); setFormData({}); fetchServices(); }
    else setMsg("❌ Error saving");
  };

  const deleteService = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await fetch(`${API}/api/admin/services/${id}`, { method: "DELETE", headers });
    fetchServices();
  };

  const seedData = async () => {
    const r = await fetch(`${API}/api/admin/seed`, { method: "POST", headers });
    const d = await r.json();
    setMsg(d.message || d.error);
    fetchStats();
  };

  const logout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div style={s.page}>
      <div style={s.container}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <div>
            <h1 style={{ fontFamily: "'Poppins',sans-serif", fontSize: "26px", fontWeight: "800", color: "#1a1a1a", margin: 0 }}>
              Admin Panel
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px", margin: "4px 0 0" }}>TechSphere Management Dashboard</p>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button style={s.btn("#10b981")} onClick={seedData}>Seed Data</button>
            <button style={s.btn("#ef4444")} onClick={logout}>Logout</button>
          </div>
        </div>

        {msg && (
          <div style={{ padding: "12px 16px", borderRadius: "8px", marginBottom: "16px",
            background: msg.includes("✅") ? "#d1fae5" : "#fee2e2",
            color: msg.includes("✅") ? "#065f46" : "#991b1b", fontSize: "14px" }}
            onClick={() => setMsg("")}>{msg} (click to dismiss)</div>
        )}

        {/* Tabs */}
        <div style={{ marginBottom: "24px", flexWrap: "wrap", display: "flex", gap: "4px" }}>
          {["dashboard", "applications", "courses", "services", "users"].map(t => (
            <button key={t} style={s.tab(tab === t)} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* DASHBOARD */}
        {tab === "dashboard" && stats && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "16px", marginBottom: "24px" }}>
              {[
                { label: "Total Applications", value: stats.totalApps, color: "#0056D2", bg: "#eff6ff" },
                { label: "Pending Review", value: stats.pending, color: "#d97706", bg: "#fffbeb" },
                { label: "Accepted", value: stats.accepted, color: "#16a34a", bg: "#f0fdf4" },
                { label: "Rejected", value: stats.rejected, color: "#dc2626", bg: "#fef2f2" },
                { label: "Active Courses", value: stats.totalCourses, color: "#7c3aed", bg: "#f5f3ff" },
                { label: "Registered Users", value: stats.totalUsers, color: "#0891b2", bg: "#ecfeff" },
              ].map((item) => (
                <div key={item.label} style={{ ...s.card, background: item.bg, padding: "20px", textAlign: "center", marginBottom: 0 }}>
                  <div style={{ fontSize: "32px", fontWeight: "800", color: item.color }}>{item.value}</div>
                  <div style={{ fontSize: "13px", color: "#374151", marginTop: "4px", fontWeight: "600" }}>{item.label}</div>
                </div>
              ))}
            </div>
            <div style={s.card}>
              <h3 style={{ margin: "0 0 8px", fontFamily: "'Poppins',sans-serif" }}>Quick Actions</h3>
              <p style={{ color: "#6b7280", fontSize: "14px" }}>
                Use the tabs above to manage applications, courses, and services. Click <b>Seed Data</b> to add sample courses and services if the database is empty.
              </p>
            </div>
          </div>
        )}

        {/* APPLICATIONS */}
        {tab === "applications" && (
          <div>
            <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
              {["all", "pending", "reviewed", "accepted", "rejected"].map(st => (
                <button key={st} style={s.tab(filterStatus === st)} onClick={() => setFilterStatus(st)}>
                  {st.charAt(0).toUpperCase() + st.slice(1)}
                </button>
              ))}
            </div>
            {applications.length === 0 ? (
              <div style={s.card}><p style={{ color: "#6b7280", textAlign: "center" }}>No applications found.</p></div>
            ) : (
              applications.map((app) => (
                <div key={app._id} style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                      <b style={{ fontSize: "16px" }}>{app.name}</b>
                      <span style={s.badge(app.status)}>{app.status}</span>
                    </div>
                    <div style={{ color: "#6b7280", fontSize: "13px" }}>
                      <div>{app.email} &nbsp;|&nbsp; {app.course}</div>
                      {app.message && <div style={{ marginTop: "4px", color: "#374151" }}>{app.message}</div>}
                      {app.resumeUrl && <a href={`${API}${app.resumeUrl}`} target="_blank" rel="noreferrer" style={{ color: "#0056D2" }}>📎 View Resume</a>}
                      <div style={{ marginTop: "4px" }}>{new Date(app.createdAt).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" })}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {["pending", "reviewed", "accepted", "rejected"].filter(s => s !== app.status).map(st => (
                      <button key={st} style={s.btn(
                        st === "accepted" ? "#16a34a" : st === "rejected" ? "#dc2626" : st === "reviewed" ? "#2563eb" : "#6b7280"
                      )} onClick={() => updateAppStatus(app._id, st)}>
                        {st.charAt(0).toUpperCase() + st.slice(1)}
                      </button>
                    ))}
                    <button style={s.btn("#ef4444")} onClick={() => deleteApp(app._id)}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* COURSES */}
        {tab === "courses" && (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
              <button style={s.btn()} onClick={() => { setShowForm(true); setEditing(null); setFormData({}); }}>+ Add Course</button>
            </div>

            {showForm && (
              <div style={{ ...s.card, border: "2px solid #0056D2", marginBottom: "20px" }}>
                <h3 style={{ margin: "0 0 16px", fontFamily: "'Poppins',sans-serif" }}>{editing ? "Edit Course" : "New Course"}</h3>
                {[["title","Title"],["description","Description"],["duration","Duration (e.g. 3 months)"],
                  ["price","Price (e.g. PKR 25,000)"],["level","Level (Beginner/Intermediate/Advanced)"],
                  ["category","Category"],["image","Image URL"]].map(([k, label]) => (
                  <input key={k} style={s.input} placeholder={label} value={formData[k] || ""}
                    onChange={(e) => setFormData({ ...formData, [k]: e.target.value })} />
                ))}
                <div style={{ display: "flex", gap: "8px" }}>
                  <button style={s.btn("#16a34a")} onClick={saveCourse}>Save Course</button>
                  <button style={s.btn("#6b7280")} onClick={() => { setShowForm(false); setEditing(null); setFormData({}); }}>Cancel</button>
                </div>
              </div>
            )}

            {courses.length === 0 ? (
              <div style={s.card}><p style={{ color: "#6b7280", textAlign: "center" }}>No courses yet. Click "Seed Data" or add manually.</p></div>
            ) : (
              courses.map((c) => (
                <div key={c._id} style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ flex: 1 }}>
                    <b style={{ fontSize: "15px" }}>{c.title}</b>
                    <div style={{ color: "#6b7280", fontSize: "13px", marginTop: "4px" }}>
                      {c.category} &nbsp;|&nbsp; {c.level} &nbsp;|&nbsp; {c.duration} &nbsp;|&nbsp; {c.price} &nbsp;|&nbsp; ⭐ {c.rating} &nbsp;|&nbsp; {c.enrolled} enrolled
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button style={s.btn("#f59e0b","#1a1a1a")} onClick={() => {
                      setEditing(c._id);
                      setFormData({ title: c.title, description: c.description, duration: c.duration,
                        price: c.price, level: c.level, category: c.category, image: c.image });
                      setShowForm(true);
                    }}>Edit</button>
                    <button style={s.btn("#ef4444")} onClick={() => deleteCourse(c._id)}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* SERVICES */}
        {tab === "services" && (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
              <button style={s.btn()} onClick={() => { setShowForm(true); setEditing(null); setFormData({}); }}>+ Add Service</button>
            </div>

            {showForm && (
              <div style={{ ...s.card, border: "2px solid #0056D2", marginBottom: "20px" }}>
                <h3 style={{ margin: "0 0 16px", fontFamily: "'Poppins',sans-serif" }}>{editing ? "Edit Service" : "New Service"}</h3>
                {[["title","Title"],["description","Description"],["icon","Icon Emoji"]].map(([k, label]) => (
                  <input key={k} style={s.input} placeholder={label} value={formData[k] || ""}
                    onChange={(e) => setFormData({ ...formData, [k]: e.target.value })} />
                ))}
                <input style={s.input} placeholder="Features (comma separated: SEO, React, Mobile First)"
                  value={Array.isArray(formData.features) ? formData.features.join(", ") : formData.features || ""}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })} />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button style={s.btn("#16a34a")} onClick={saveService}>Save Service</button>
                  <button style={s.btn("#6b7280")} onClick={() => { setShowForm(false); setEditing(null); setFormData({}); }}>Cancel</button>
                </div>
              </div>
            )}

            {services.length === 0 ? (
              <div style={s.card}><p style={{ color: "#6b7280", textAlign: "center" }}>No services yet. Click "Seed Data" or add manually.</p></div>
            ) : (
              services.map((sv) => (
                <div key={sv._id} style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <div>
                    <b style={{ fontSize: "15px" }}>{sv.icon} {sv.title}</b>
                    <div style={{ color: "#6b7280", fontSize: "13px", marginTop: "4px" }}>{sv.description}</div>
                    {sv.features && <div style={{ fontSize: "12px", marginTop: "4px", color: "#374151" }}>{sv.features.join(" · ")}</div>}
                  </div>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button style={s.btn("#f59e0b","#1a1a1a")} onClick={() => {
                      setEditing(sv._id);
                      setFormData({ title: sv.title, description: sv.description, icon: sv.icon, features: sv.features?.join(", ") || "" });
                      setShowForm(true);
                    }}>Edit</button>
                    <button style={s.btn("#ef4444")} onClick={() => deleteService(sv._id)}>Delete</button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* USERS */}
        {tab === "users" && (
          <div>
            {users.length === 0 ? (
              <div style={s.card}><p style={{ color: "#6b7280", textAlign: "center" }}>No registered users yet.</p></div>
            ) : (
              users.map((u) => (
                <div key={u._id} style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <b>{u.name}</b>
                    <div style={{ color: "#6b7280", fontSize: "13px" }}>{u.email} &nbsp;|&nbsp; Role: {u.role}</div>
                    <div style={{ color: "#9ca3af", fontSize: "12px" }}>Joined: {new Date(u.createdAt).toLocaleDateString()}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
